import type { QuotationStatus } from '@/types/api'

/**
 * How a quotation's status reads on screen, and what it lets a person do.
 *
 * These mirror `QuotationLifecycle` on the server. The server is the authority -
 * every one of these actions is checked again there - but a builder that offers
 * "Send" on a draft teaches reps to distrust the buttons, so the two agree.
 */

type Tone = 'neutral' | 'positive' | 'warning' | 'critical' | 'accent'

interface Presentation {
  label: string
  tone: Tone
  /** One line under the badge, saying what state the quotation is actually in. */
  hint: string
}

const PRESENTATION: Record<QuotationStatus, Presentation> = {
  Draft: { label: 'Draft', tone: 'neutral', hint: 'Not submitted. Nobody outside has seen it.' },
  PendingApproval: {
    label: 'Pending approval',
    tone: 'warning',
    hint: 'With its approvers. Editing needs it withdrawn first.',
  },
  Approved: { label: 'Approved', tone: 'positive', hint: 'Cleared the chain and ready to send.' },
  Rejected: {
    label: 'Changes needed',
    tone: 'critical',
    hint: 'Sent back with a reason. Withdraw it to a draft and fix what was wrong.',
  },
  Sent: { label: 'Sent', tone: 'accent', hint: 'With the customer, waiting on an answer.' },
  Accepted: { label: 'Accepted', tone: 'accent', hint: 'The customer said yes. Record the outcome to close it.' },
  Declined: { label: 'Declined', tone: 'neutral', hint: 'The customer said no.' },
  Expired: {
    label: 'Expired',
    tone: 'neutral',
    hint: 'Passed its validity date with no answer. Counts as a loss.',
  },
  Won: { label: 'Won', tone: 'positive', hint: 'Closed as business.' },
  Lost: { label: 'Lost', tone: 'neutral', hint: 'Closed without the business.' },
  Cancelled: { label: 'Cancelled', tone: 'neutral', hint: 'Withdrawn from the pipeline entirely.' },
}

export function presentation(status: QuotationStatus): Presentation {
  return PRESENTATION[status]
}

/** Issued and not yet resolved. A draft is an intention, not pipeline. */
export function isOpen(status: QuotationStatus): boolean {
  return status === 'PendingApproval' || status === 'Approved' || status === 'Sent' || status === 'Accepted'
}

export function isClosed(status: QuotationStatus): boolean {
  return (
    status === 'Won' || status === 'Lost' || status === 'Declined' || status === 'Expired'
  )
}

export function canEdit(status: QuotationStatus): boolean {
  return status === 'Draft'
}

export function canSubmit(status: QuotationStatus): boolean {
  return status === 'Draft'
}

/** Rejected belongs here too: a rejection is not a dead end, it is a round trip. */
export function canWithdraw(status: QuotationStatus): boolean {
  return status === 'PendingApproval' || status === 'Rejected'
}

/**
 * Approved or sent, taken back to draft for changes - `QuotationLifecycle.CanReturnToDraft`.
 *
 * <p>
 * Not once the customer has accepted: they signed what it says now. A copy signed on paper
 * leaves the status at Sent, so the API refuses that case too, with the reason.
 * </p>
 */
export function canReEdit(status: QuotationStatus): boolean {
  return status === 'Approved' || status === 'Sent'
}

/**
 * Cancellable with a reason: anything open, and a draft the customer was already sent before
 * it was taken back for changes. That draft is not deletable, so this is its way out.
 */
export function canCancel(status: QuotationStatus, sentAt: string | null): boolean {
  return isOpen(status) || (status === 'Draft' && sentAt !== null)
}

/** A draft raised by mistake. One the customer has seen is cancelled instead. */
export function canDeleteDraft(status: QuotationStatus, sentAt: string | null): boolean {
  return canEdit(status) && sentAt === null
}

export function canSend(status: QuotationStatus): boolean {
  return status === 'Approved' || status === 'Sent'
}

/**
 * Approved is accepted as well as Sent, because a deal is sometimes closed in the
 * room. Refusing until somebody remembers to press Send would only teach reps to
 * record outcomes late, which is the one thing the figures cannot survive.
 */
export function canRecordOutcome(status: QuotationStatus): boolean {
  return status === 'Approved' || status === 'Sent' || status === 'Accepted'
}

/**
 * Whether a loss is one of the outcomes on offer.
 *
 * <p>
 * Not once the customer has accepted. An acceptance is a signed document, and recording
 * the quotation lost would contradict it - the way back out of one is a cancellation with
 * a stated reason, not a quiet reclassification. The API refuses it either way; this is
 * what stops the screen offering a button that only ever returns an error.
 * </p>
 */
export function canRecordLoss(status: QuotationStatus): boolean {
  return canRecordOutcome(status) && status !== 'Accepted'
}

/** Anything short of approved renders watermarked, so the preview is never mistaken for the real thing. */
export function isProvisional(status: QuotationStatus): boolean {
  return !(status === 'Approved' || status === 'Sent' || status === 'Accepted' || status === 'Won')
}
